/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 */

// https://v1.chakra-ui.com/docs/styled-system/theming/advanced#multipart-or-composite-component

import { modalAnatomy } from '@chakra-ui/anatomy'
import type { ComponentMultiStyleConfig } from '@chakra-ui/theme'
import { PartsStyleFunction, PartsStyleObject } from '@chakra-ui/theme-tools'
import { ALPHA_48, Z_INDEX_MODAL_BACKDROP } from '@zcode-ui/themes'

const focus = {
  outline: 'none',
  borderColor: 'neutral.500',
  boxShadow: '0 0 0 var(--chakra-space-1) var(--chakra-colors-neutral-200)',
}

const focusNone = {
  outline: 'none',
  border: 'none',
  boxShadow: 'none',
}

const baseStyle: PartsStyleObject<typeof modalAnatomy> = {
  closeButton: {
    _focus: {
      ...focusNone,
      svg: {
        base: focusNone,
        desktop: focusNone,
      },
    },
    _focusVisible: {
      ...focus,
      svg: {
        base: focus,
        desktop: focusNone,
      },
    },
  },
}

const variantLeftClose: PartsStyleFunction<typeof modalAnatomy> = (props) => {
  return {
    body: {
      overflowY: 'auto',
      pb: { base: 24, desktop: 6 },
      pl: { base: 6, desktop: 6 },
      pr: { base: 6, desktop: 6 },
      pt: 8,
    },
    closeButton: {
      _focus: {
        ...focusNone,
        svg: focusNone,
      },
      _focusVisible: {
        outline: 'none',
        borderColor: 'neutral.500',
        boxShadow: {
          base: 'none',
          desktop: '0 0 0 var(--chakra-space-1) var(--chakra-colors-neutral-200)',
        },
      },
      _hover: {
        backgroundColor: 'gray.150',
      },
      alignItems: 'center',
      background: 'transparent',
      border: 0,
      borderRadius: { base: 'full', desktop: 'base' },
      cursor: 'pointer',
      display: 'flex',
      height: { base: /* minimum tap size for mobile */ '44px', desktop: 5 },
      left: { base: 2, md: 'auto' },
      justifyContent: 'center',
      p: 0,
      position: 'absolute',
      right: { base: 'auto', md: 5 },
      svg: {
        borderRadius: '3px',
        height: { base: '18px', desktop: 5 },
        p: '5px',
        width: { base: '18px', desktop: 5 },
      },
      top: '50%',
      transform: 'translateY(-50%)',
      transition: 'background 150ms ease-in, box-shadow 150ms ease-in',
      width: { base: /* minimum tap size for mobile */ '44px', desktop: 5 },
    },
    dialog: {
      backgroundColor: 'gray.50',
      height: 'max-content',
      marginY: 0,
      top: props.size === 'full' ? 0 : { md: -10 },
    },
    dialogContainer: {
      height: '100vh',
      '@supports(min-height: -webkit-fill-available)': {
        // override chakra default styling
        height: '100vh',
      },
      marginTop: props.size === 'full' ? 0 : { md: 10 },
    },
    header: {
      backgroundColor: 'inherit',
      borderBottomColor: 'gray.150',
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
      p: 6,
      position: 'sticky',
      top: 0,
      zIndex: 'modal',
      h2: {
        // should use Display 24 once Murph has that ready
        color: 'gray.800',
        fontFamily: 'body',
        fontSize: 'sm',
        fontWeight: 650,
        letterSpacing: '1.96px',
        lineHeight: 5,
        textAlign: 'center',
        textTransform: 'uppercase',
      },
    },
    overlay: {
      backgroundColor: `#000000${ALPHA_48}`,
      zIndex: Z_INDEX_MODAL_BACKDROP,
    },
  }
}

const sizeFull: PartsStyleFunction<typeof modalAnatomy> = () => ({
  dialog: { borderRadius: 0 },
})

const sizes = {
  full: sizeFull,
}

const variants = {
  leftClose: variantLeftClose,
}

export const Z4Modal: ComponentMultiStyleConfig = {
  baseStyle,
  parts: modalAnatomy.keys,
  sizes,
  variants,
}
